import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  HostListener,
  OnDestroy,
  Output,
  ViewContainerRef,
} from '@angular/core';
import { Timer, TimeUtils } from '@freelancer/time-utils';
import { FreelancerBreakpointValues } from '@freelancer/ui/breakpoints';

export interface CalloutTriggerOpen {
  isPinnedOnClick?: boolean;
}

@Component({
  selector: 'fl-callout-trigger',
  template: ` <ng-content></ng-content> `,
  styleUrls: ['./callout-trigger.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalloutTriggerComponent implements OnDestroy {
  @Output() trigger = new EventEmitter<MouseEvent>();
  @Output() open = new EventEmitter<CalloutTriggerOpen>();
  @Output() close = new EventEmitter<void>();

  hover: boolean;
  mobileLongPress: boolean;

  @HostBinding('attr.data-mobile-force-click') mobileForceClick: boolean;

  private longPressTimeout: Timer;
  readonly LONG_PRESS_DELAY = 500;

  constructor(
    public containerRef: ViewContainerRef,
    private timeUtils: TimeUtils,
  ) {}

  @HostListener('click', ['$event'])
  handleTriggerClick(event: MouseEvent) {
    /**
     * Force open if hover is set to true
     * to support touch devices as well.
     *
     * Note: Clicking the trigger again or outside
     * the callout will close the callout.
     */
    if (!this.isMobileViewport() && this.hover) {
      this.open.emit({ isPinnedOnClick: true });

      return;
    }

    if (
      this.isMobileViewport() &&
      this.mobileLongPress &&
      !this.mobileForceClick
    ) {
      return;
    }

    this.trigger.emit(event);
  }

  @HostListener('mouseenter')
  handleTriggerMouseover() {
    // Prevent this from running on touch device as it suppress
    // click event from triggering
    if (!this.isMobileViewport() && !this.isTouchDevice()) {
      this.open.emit();
    }
  }

  @HostListener('mouseleave')
  handleTriggerMouseout() {
    if (!this.isMobileViewport() && !this.isTouchDevice()) {
      this.close.emit();
    }
  }

  @HostListener('touchstart')
  handleTouchStart() {
    if (!this.mobileLongPress) {
      return;
    }

    if (this.longPressTimeout) {
      clearTimeout(this.longPressTimeout);
    }

    this.longPressTimeout = this.timeUtils.setTimeout(() => {
      this.open.emit();
    }, this.LONG_PRESS_DELAY);
  }

  @HostListener('touchend')
  handlleTouchEnd() {
    if (this.longPressTimeout) {
      clearTimeout(this.longPressTimeout);
    }
  }

  getElementBoundingRect() {
    return (this.containerRef.element
      .nativeElement as HTMLElement).getBoundingClientRect();
  }

  setOptions(options: {
    hover: boolean;
    mobileForceClick: boolean;
    mobileLongPress: boolean;
  }) {
    this.hover = options.hover;
    this.mobileForceClick = options.mobileForceClick;
    this.mobileLongPress = options.mobileLongPress;
  }

  ngOnDestroy() {
    if (this.longPressTimeout) {
      clearTimeout(this.longPressTimeout);
    }
  }

  private isMobileViewport() {
    return window.innerWidth < parseInt(FreelancerBreakpointValues.TABLET, 10);
  }

  private isTouchDevice(): boolean {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }
}
